import React from 'react';
import PropTypes from 'prop-types';
import { Router } from 'react-router-dom';
import { createBrowserHistory } from 'history';
import { compose, graphql } from 'react-apollo';

/**
 * Options
 */

import OPTIONS from './Options.js';

/**
 * Routes
 */

import Routes from './Routes';
import RouteListener from './router/RouteListener';

/**
 * Mutations
 */

import { qaMutation } from './mutations/qa';
import { classifyMutation } from './mutations/classify';

/**
 * Contexts and "Stores"
 */

import AppContext from './contexts/app';
import AppStore from './stores/app';

import ClinicalDataContext from './contexts/clinicalData';
import ClinicalDataStore from './stores/clinicalData';

import ConditionsDataContext from './contexts/conditionsData';
import ConditionsDataStore from './stores/conditionsData';

import UserContext from './contexts/user';
import UserStore from './stores/user';

/**
 * Layout
 */

import Header from './layout/Header';
import Footer from './layout/Footer';

/**
 * OTHERS
 */

import SegmentAnalytics from '../../helpers/SegmentAnalytics.js';
import AutoScroll from 'shared/AutoScroll';
import { wasOnlyModalOpenOrClosed } from 'shared/Modal';
import Loader from './components/ContentLoader';

class App extends React.Component {
  app;
  clinicalData;
  conditionsData;
  user;
  history;

  photoInstructionsShown = false;
  cancelMutations = false;

  constructor(props) {
    super(props);

    if (props.segmentAnalyticsKey) {
      SegmentAnalytics.load(props.segmentAnalyticsKey);
    }

    this.history = createBrowserHistory({ basename: '/' });

    this.app = new AppStore(this);
    this.clinicalData = new ClinicalDataStore(this);
    this.conditionsData = new ConditionsDataStore(this);
    this.user = new UserStore(this);

    if (props.currentUser) {
      this.user.logIn(props.currentUser, false);
    }

    this.app.basePath = props.basePath;
    this.app.baseUrl = (props.baseUrl) ? props.baseUrl.replace(/\/$/, '') : undefined; // Stripping forward slash
    this.app.isOfflineMode = props.isOfflineMode;
    this.app.recaptchaSiteKey = props.recaptchaSiteKey;

    this.app.healthPages = {
      baseUrl: props.healthPagesUrl, // 'http://localhost:4000/health' to test with a local jekyll server
      basePath: '/health',
      conditionPagesPath: '/conditions'
    };

    this.reset();

    this.state = {
      loading: false,
      app: this.app,
      clinicalData: this.clinicalData,
      conditionsData: this.conditionsData,
      user: this.user,
      history: this.history
    };
  }

  reset(goHome) {
    this.clinicalData.clearAllData();

    if (goHome) { this.history.push('/'); }
  }

  // this also fires on the first render
  handleRouteChange = (location, prevLocation) => {
    if (wasOnlyModalOpenOrClosed(location, prevLocation)) return;

    this.trackPageChange(location.pathname);
    this.app.cancelMutations = true;

    if (this.state.loading) {
      this.setState({ loading: false });
    }
  }

  trackPageChange(path) {
    if (path.startsWith('/health/conditions/')) {
      SegmentAnalytics.page('/health/conditions/*');
    } else {
      SegmentAnalytics.page(path);
    }
  }

  render() {
    return (
      <AppContext.Provider value={this.app}>
        <UserContext.Provider value={this.user}>
          <ClinicalDataContext.Provider value={this.clinicalData}>
            <ConditionsDataContext.Provider value={this.conditionsData}>

              <Router history={this.history}>
                <RouteListener onRouteChange={this.handleRouteChange} />
                <AutoScroll loading={this.state.loading} />

                <Header />

                <Loader loading={this.state.loading}>
                  <Routes />
                </Loader>

                <Footer />
              </Router>

            </ConditionsDataContext.Provider>
          </ClinicalDataContext.Provider>
        </UserContext.Provider>
      </AppContext.Provider>
    );
  }

  onError(error) {
    SegmentAnalytics.track('Error', {
      error: error.toString()
    });

    this.app.error = error;
    this.history.push('/error');
  }

  onImageUpload(image) {
    this.clinicalData.setImage(image);
    this.history.push('/snap-a-photo');
  }

  onFirstGate(result) {
    this.clinicalData.setFirstGateResult(result);

    SegmentAnalytics.track('First-gate result', {
      result: result.type
    });

    if (!result.error) {
      this.history.push('/patient');
    }
  }

  onPatientDataSet() {
    this.history.push('/bodylocator');
  }

  async onBodyLocationSet() {
    const isMacroscopic = this.clinicalData.firstGateResult.type === 'macroscopic';

    if (isMacroscopic) {
      await this.qaImage();
      this.history.push('/qa');
    } else {
      this.classifyImage();
    }
  }

  onQaAnswersSet() {
    const numQuestionsAnswered = this.clinicalData.qa.answers.length;
    const reachedMaxQuestions = !OPTIONS.qa.maxQuestions ? false : (numQuestionsAnswered >= OPTIONS.qa.maxQuestions);

    if (reachedMaxQuestions) {
      this.classifyImage();
    } else {
      this.qaImage();
    }
  }

  onViewAnyway() {
    this.history.push('/patient');
  }

  async qaImage() {
    if (!this.clinicalData.firstGateResult) {
      this.history.push('/snap-a-photo');
      return;
    }

    const qaResults = await this.qaWithModel('macroscopic', {
      clinicalEvidence: this.clinicalData.allPatientData()
    });

    this.saveQaResults(qaResults);

    if (qaResults.images[0].question) {
      this.history.push('/qa');
    } else {
      this.classifyImage();
    }
  }

  async classifyImage() {
    if (!this.user.currentUser) {
      this.history.push('/create-account');
      return;
    }

    if (!this.clinicalData.firstGateResult) {
      this.history.push('/snap-a-photo');
      return;
    }

    this.setState({ loading: true }, () => {
      this.app.cancelMutations = false;
    });

    this.clinicalData.resolveInferenceType(this.user.isClinician);

    const classifyResult = await this.classifyWithModel(this.clinicalData.inferenceType, {
      clinicalEvidence: this.clinicalData.allPatientData(),
      saveScan: true
    });

    this.saveResults(classifyResult);

    SegmentAnalytics.track('Model predictions', this.inferenceAnalyticsData(this.clinicalData.inference));
    this.history.push('/results');
  }

  saveQaResults(results) {
    const question = results.images[0].question;

    this.clinicalData.addQaQuestion(question);
  }

  saveResults(results) {
    const { isOfflineMode } = this.app;
    const predictions = this.sortPredictions(results.images[0].predictions);

    this.clinicalData.setInference({
      snapId: results.id,
      image: results.images[0],
      predictions: (isOfflineMode) ? predictions : predictions.slice(0, OPTIONS.results.macroscopic.maxPredictions)
    });
  }

  inferenceAnalyticsData(inference) {
    const predictionIDs = inference.predictions.map(s => s.condition.id);
    return {
      result_1: predictionIDs[0],
      result_2: predictionIDs[1],
      result_3: predictionIDs[2],
      result_4: predictionIDs[3],
      result_5: predictionIDs[4],
      results: predictionIDs
    };
  }

  sortPredictions(predictions) {
    return (predictions || []).slice().sort((a, b) => b.probability - a.probability);
  }

  async qaWithModel(model, extraProps = {}) {
    try {
      const { data } = await this.props.qaMutation({
        variables: {
          input: {
            model: model,
            images: [{
              id: this.clinicalData.image.id,
              crop: this.clinicalData.image.crop
            }],
            ...extraProps
          }
        }
      });

      return data.snapQa;
    } catch (error) {
      this.onError(error);
    }
  }

  async classifyWithModel(model, extraProps = {}) {
    try {
      const { data } = await this.props.classifyMutation({
        variables: {
          input: {
            model: model,
            images: [{
              id: this.clinicalData.image.id,
              crop: this.clinicalData.image.crop
            }],
            ...extraProps
          }
        }
      });

      return data.snapClassify;
    } catch (error) {
      this.onError(error);
    }
  }
}

App.defaultProps = {};

App.propTypes = {
  qaMutation: PropTypes.func.isRequired,
  classifyMutation: PropTypes.func.isRequired,
  countryCode: PropTypes.string,
  currentUser: PropTypes.shape({
    id: PropTypes.number.isRequired,
    email: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    accountType: PropTypes.string.isRequired
  }),
  baseUrl: PropTypes.string,
  basePath: PropTypes.string,
  healthPagesUrl: PropTypes.string,
  segmentAnalyticsKey: PropTypes.string,
  isOfflineMode: PropTypes.bool,
  recaptchaSiteKey: PropTypes.string
};

const AppWithMutations = compose(
  graphql(qaMutation, {
    name: 'qaMutation'
  }),
  graphql(classifyMutation, {
    name: 'classifyMutation'
  })
)(App);

export { App };
export default AppWithMutations;
